import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Leaf, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginSupplier } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [mode, setMode] = useState('client'); // 'client' | 'supplier'
    const [formData, setFormData] = useState({ email: '', password: '' });

    const from = location.state?.from?.pathname || '/dashboard';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const normalizedPassword = formData.password.normalize('NFC').trim();
            if (mode === 'supplier') {
                const user = await loginSupplier(formData.email.trim(), normalizedPassword);
                toast.success(`Bienvenue, ${user.contact_name || user.company_name} !`);
                navigate('/fournisseur');
            } else {
                const user = await login(formData.email.trim(), normalizedPassword);
                toast.success(`Bienvenue, ${user.contact_name} !`);
                navigate(user.role === 'admin' ? '/admin' : from);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-sand bg-botanical flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
            {/* Botanical decorations */}
            <div className="absolute top-0 right-0 w-96 h-96 opacity-10">
                <svg viewBox="0 0 200 200" fill="currentColor" className="text-jungle w-full h-full">
                    <path d="M100 20c-10 40-40 70-80 80 40 10 70 40 80 80 10-40 40-70 80-80-40-10-70-40-80-80z" />
                </svg>
            </div>
            <div className="absolute bottom-0 left-0 w-64 h-64 opacity-10">
                <svg viewBox="0 0 200 200" fill="currentColor" className="text-leaf w-full h-full">
                    <path d="M100 30c-8 32-32 56-64 64 32 8 56 32 64 64 8-32 32-56 64-64-32-8-56-32-64-64z" />
                </svg>
            </div>
            
            <Card className="w-full max-w-md border-0 shadow-xl bg-white/80 backdrop-blur-xl rounded-2xl relative z-10">
                <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-jungle flex items-center justify-center mb-4">
                        <Leaf className="w-9 h-9 text-sand" />
                    </div>
                    <CardTitle className="font-heading text-2xl text-jungle">Connexion</CardTitle>
                    <CardDescription>
                        Accédez à votre espace professionnel
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Mode Toggle */}
                    <div className="flex rounded-xl bg-gray-100 p-1 mb-5" data-testid="login-mode-toggle">
                        <button
                            type="button"
                            onClick={() => setMode('client')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'client' ? 'bg-white text-jungle shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            data-testid="login-mode-client"
                        >
                            <Leaf className="w-4 h-4" />
                            Client / Admin
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('supplier')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${mode === 'supplier' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            data-testid="login-mode-supplier"
                        >
                            <Truck className="w-4 h-4" />
                            Fournisseur
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-jungle">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.fr"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="bg-white/80 border-jungle/20 focus:border-leaf rounded-xl"
                                data-testid="email-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-jungle">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    required
                                    className="bg-white/80 border-jungle/20 focus:border-leaf rounded-xl"
                                    data-testid="password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                                </Button>
                            </div>
                        </div>
                        <Button 
                            type="submit" 
                            className={`w-full rounded-xl h-11 ${mode === 'supplier' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-jungle hover:bg-jungle/90 text-sand'}`}
                            disabled={loading}
                            data-testid="login-submit"
                        >
                            {loading ? 'Connexion...' : mode === 'supplier' ? 'Accéder à l\'espace fournisseur' : 'Se connecter'}
                        </Button>
                    </form>

                    {mode === 'client' && (
                        <>
                            <div className="mt-4 text-center">
                                <Link to="/mot-de-passe-oublie" className="text-sm text-muted-foreground hover:text-leaf transition-colors" data-testid="forgot-password-link">
                                    Mot de passe oublié ?
                                </Link>
                            </div>
                            <div className="mt-4 text-center text-sm">
                                <span className="text-muted-foreground">Pas encore de compte ? </span>
                                <Link to="/inscription" className="text-leaf hover:underline font-medium" data-testid="register-link">
                                    Créer un compte
                                </Link>
                            </div>
                        </>
                    )}

                    {mode === 'supplier' && (
                        <div className="mt-4 text-center text-sm text-gray-500">
                            Vos identifiants vous ont été fournis par l'administrateur PRIXDEAL.
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
